import React, { createContext, type PropsWithChildren, useMemo, useState } from "react";
import { useLocalStorage, useMediaQuery } from "usehooks-ts";
import { System } from "@remi/types/system";
import { resolveThemePreference, Theme, type ThemePreference } from "@remi/types/theme";

import { RemiTooltipProvider } from "../components/RemiTooltip";
import { useEffect } from "../hooks/useEffect";

type ThemeContextProps = {
  theme: Theme;
  themePreference: ThemePreference;
  system: System;
  setThemePreference: (value: ThemePreference) => void;
  setSystem: (value: System) => void;
};

export const ThemeContext = createContext<ThemeContextProps>({
  theme: Theme.LIGHT,
  themePreference: Theme.LIGHT,
  system: System.PORTAL,
  setThemePreference: () => {},
  setSystem: () => {},
});

const LOCAL_STORAGE_KEY = "theme";
const PREFERS_DARK_QUERY = "(prefers-color-scheme: dark)";

export const ThemeProvider = ({
  defaultTheme,
  defaultSystem,
  persist = true,
  children,
}: PropsWithChildren<{
  defaultTheme: Theme;
  defaultSystem: System;
  /** When false, the theme is fixed to `defaultTheme` and not read from / written to localStorage. */
  persist?: boolean;
}>) => {
  // `useLocalStorage` always runs (Rules of Hooks) — when `persist` is false its
  // value and setter are ignored. `initializeWithValue` is tied to `persist`:
  // main app (persist=true) reads synchronously on first render so users who
  // saved DARK never see a LIGHT→DARK flash; portal (persist=false) would not
  // use the value anyway, and `false` keeps SSR safe for future consumers.
  const [persistedPreference, setPersistedPreference] = useLocalStorage<ThemePreference>(
    LOCAL_STORAGE_KEY,
    defaultTheme,
    {
      initializeWithValue: persist,
    },
  );
  const [statePreference, setStatePreference] = useState<ThemePreference>(defaultTheme);
  const themePreference = persist ? persistedPreference : statePreference;
  const setThemePreference = persist ? setPersistedPreference : setStatePreference;
  const [system, setSystem] = useState<System>(defaultSystem);

  const prefersDark = useMediaQuery(PREFERS_DARK_QUERY, { defaultValue: false, initializeWithValue: persist });
  const theme = resolveThemePreference(themePreference, prefersDark);

  useEffect(() => {
    const lowercaseTheme = theme.toLowerCase();
    document.body.className = `${lowercaseTheme} bg-background-primary text-text-primary`;
    document.body.dataset.theme = lowercaseTheme;
  }, [theme]);

  const value = useMemo(
    () => ({ theme, themePreference, system, setThemePreference, setSystem }),
    [theme, themePreference, system, setThemePreference],
  );

  // skipDelayDuration only spans tooltips under one Provider; this is the root every app already mounts.
  return (
    <ThemeContext.Provider value={value}>
      <RemiTooltipProvider>{children}</RemiTooltipProvider>
    </ThemeContext.Provider>
  );
};
